import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Autocomplete from '@components/Autocomplete';
import { Typography } from '@components/Typography';
import { Input } from '@components/Input';
import ComboLocationSelect, { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { Select } from '@components/Select';
import { Switch } from '@components/Switch';
import BusinessHoursSelection from '@features/BusinessHoursSelection';
import { useTranslation, useChannels, useLocations } from '@hooks';
import { CHANNEL } from '@constants/channels';
import { labelsActions } from '@actions';
import CollectInformationBlock from './CollectInformation';

const Container = styled('div')({
    display: 'grid',
    gridGap: '8px',
    gridTemplateColumns: '100%',
});

const EmailSelect = styled(Select)({
    '& .MuiFormControl-root': {
        paddingBottom: '8px !important',
    },
    '& .MuiFormHelperText-root': {
        bottom: '-11px !important',
    },
});

const LiveChatForm = () => {
    const { t } = useTranslation();

    const { data: labels } = useSelector(state => state.labels);
    const brands = useSelector(state => state.brands.data);

    const dispatch = useDispatch();
    const { getChannelsByBrand, getChannelsByLocationId, getChannelOptions } = useChannels();
    const { getSelectLocationsOptions, getLocationsOptionsByBrand } = useLocations();
    const { control, setValue, watch, selectedChannel } = useFormContext();

    const workingHoursEnabled = watch('workingHoursEnabled');
    const notifyAboutMissingConversations = watch('notifyAboutMissingConversations');
    const locationType = watch('locationType');
    const brandId = watch('brandId');
    const locationId = watch('locationId');
    const locationsEnabled = watch('locationsEnabled');

    const timeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

    useEffect(() => {
        dispatch(labelsActions.get());
    }, []);

    const smsChannelsOptions = useMemo(() => {
        const filterCb = channel => channel.type === CHANNEL.SMS || channel.type === CHANNEL.WHATSAPP;
        const availableSmsChannels =
            locationType === LOCATION_TYPE.BRAND
                ? getChannelsByBrand(brandId, filterCb)
                : getChannelsByLocationId(locationId, filterCb);
        return getChannelOptions({ out: true }, availableSmsChannels);
    }, [locationType, brandId, locationId]);

    const emailChannelsOptions = useMemo(() => {
        const filterCb = channel => channel.type === CHANNEL.EMAIL;
        const availableEmailChannels =
            locationType === LOCATION_TYPE.BRAND
                ? getChannelsByBrand(brandId, filterCb)
                : getChannelsByLocationId(locationId, filterCb);
        return getChannelOptions({ out: true, withPluspointEmail: true }, availableEmailChannels);
    }, [locationType, brandId, locationId]);

    const brandsOptions = useMemo(() => brands.map(item => ({ value: item.id, label: item.title })), [brands]);

    const locationsOptions = useMemo(() => getSelectLocationsOptions(), []);

    const brandLocationsOptions = useMemo(() => getLocationsOptionsByBrand(brandId), [brandId]);

    return (
        <Container>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.channelName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <ComboLocationSelect
                brandsOptions={brandsOptions}
                locationsOptions={locationsOptions}
                data={selectedChannel}
                updatedLocationCb={() => {
                    setValue('smsChannelId', '');
                    setValue('emailChannelId', '');
                    setValue('locationsEnabled', false, { shouldDirty: true, shouldValidate: true });
                    setValue('locations', [], { shouldDirty: true, shouldValidate: true });
                }}
            />
            {locationType === LOCATION_TYPE.BRAND && (
                <>
                    <Controller
                        control={control}
                        name="locationsEnabled"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={field.value}
                                handleChange={event => {
                                    setValue('locationsEnabled', event.target.checked, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                    if (!event.target.checked) {
                                        setValue('locations', [], {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }
                                }}
                                label={t('SettingsChannels.customLocationsSwitch')}
                                sx={{ marginTop: '-8px' }}
                            />
                        )}
                    />
                    {locationsEnabled && (
                        <Controller
                            control={control}
                            name="locations"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Autocomplete
                                    {...field}
                                    ref={ref}
                                    label={t('common.locations')}
                                    options={brandLocationsOptions}
                                    value={field.value}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    inputProps={{ style: { display: field?.value?.length > 0 ? 'none' : 'block' } }}
                                    onChangeAutoComplete={items => {
                                        setValue('locations', items.length ? items : [], {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }}
                                    groupMultiProps={{
                                        getOptionLabel: option => option.label,
                                    }}
                                    required
                                    error={error}
                                    className="locations-autocomplete"
                                />
                            )}
                        />
                    )}
                </>
            )}
            <Typography variant="subtitle1" sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                {t('FeedbackSurveys.contactDetails')}
            </Typography>
            <CollectInformationBlock labels={labels} />
            <Typography variant="subtitle1" sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                {t('common.notifications')}
            </Typography>
            <Controller
                control={control}
                name="notifyAboutMissingConversations"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => {
                            setValue('notifyAboutMissingConversations', event.target.checked, {
                                shouldDirty: true,
                            });
                            if (!event.target.checked) {
                                setValue('smsChannelId', '');
                                setValue('emailChannelId', '');
                            }
                        }}
                        label={t('SettingsChannels.missingConversationsSwitch')}
                    />
                )}
            />
            {notifyAboutMissingConversations && (
                <>
                    <Controller
                        control={control}
                        name="smsChannelId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Select
                                {...field}
                                ref={ref}
                                label={t('SettingsChannels.phoneNotificationsChannel')}
                                options={smsChannelsOptions}
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="emailChannelId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <EmailSelect
                                {...field}
                                ref={ref}
                                label={t('SettingsChannels.emailNotificationsChannel')}
                                options={emailChannelsOptions}
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                            />
                        )}
                    />
                </>
            )}
            <Typography
                variant="subtitle1"
                sx={{ marginTop: '8px', marginBottom: '16px', color: 'rgba(0, 0, 0, 0.87)' }}
            >
                {t('SettingsChannels.conversationStarters')}
            </Typography>
            <Controller
                control={control}
                name="welcomeHeading"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('common.title')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <Controller
                control={control}
                name="welcomeTagline"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.welcomeTagline')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <Typography variant="subtitle1" sx={{ marginBottom: '8px', color: 'rgba(0, 0, 0, 0.87)' }}>
                {t('Listings.businessHours')}
            </Typography>
            <Controller
                control={control}
                name="workingHoursEnabled"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => {
                            setValue('workingHoursEnabled', event.target.checked, {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                        }}
                        label={t('SettingsChannels.businessHoursSwitch')}
                    />
                )}
            />
            {workingHoursEnabled && (
                <Controller
                    control={control}
                    name="offlineMessage"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsChannels.offlineMessage')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            multiline
                            minRows={4}
                        />
                    )}
                />
            )}
            {workingHoursEnabled && (
                <Controller
                    control={control}
                    name="workingHours"
                    render={({ field, ref }) => (
                        <BusinessHoursSelection
                            {...field}
                            data={field?.value?.days}
                            ref={ref}
                            onChangeHours={value => {
                                setValue('workingHours', { timeZone, days: value }, { shouldDirty: true });
                            }}
                        />
                    )}
                />
            )}
        </Container>
    );
};

export default LiveChatForm;
